"use client";

import { useEffect, useState } from "react";
import { klaroConfig } from "@/lib/klaro-config";

type KlaroManager = {
  confirmed: boolean;
  consents: Record<string, boolean>;
  changeAll: (value: boolean) => number;
  updateConsent: (name: string, value: boolean) => void;
  saveAndApplyConsents: (eventType?: string) => void;
  watch: (watcher: { update: (manager: KlaroManager, name: string) => void }) => void;
  unwatch: (watcher: { update: (manager: KlaroManager, name: string) => void }) => void;
};

type KlaroModule = {
  default?: KlaroModule;
  getManager?: (config?: typeof klaroConfig) => KlaroManager;
};


export default function KlaroBanner() {
  const [manager, setManager] = useState<KlaroManager | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let active = true;
    let mgr: KlaroManager | null = null;
    let watcher: { update: (manager: KlaroManager, name: string) => void } | null = null;

    const init = async () => {
      const module = (await import("klaro")) as KlaroModule;
      const klaro = module.default ?? module;
      const resolvedManager = klaro.getManager?.(klaroConfig);

      if (!active || !resolvedManager) return;

      mgr = resolvedManager;
      setManager(resolvedManager);
      setVisible(!resolvedManager.confirmed);

      watcher = {
        update: (updatedManager) => {
          if (!active) return;
          setVisible(!updatedManager.confirmed);
        },
      };

      resolvedManager.watch(watcher);
    };

    void init();

    return () => {
      active = false;
      if (mgr && watcher) {
        mgr.unwatch(watcher);
      }
    };
  }, []);

  const acceptEssentialOnly = () => {
    if (!manager) return;
    manager.changeAll(false);
    manager.saveAndApplyConsents("save");
    setVisible(false);
  };

  const acceptAll = () => {
    if (!manager) return;
    manager.changeAll(true);
    manager.saveAndApplyConsents("accept");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 z-[999] w-[min(720px,calc(100%-32px))] -translate-x-1/2 rounded-2xl border border-[var(--border)] bg-white/95 p-6 shadow-[0_18px_38px_rgba(38,70,83,0.14),0_6px_12px_rgba(38,70,83,0.08)] backdrop-blur"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--brand-saffron)_25%,#fff_75%)] text-lg">
          🍪
        </div>
        <div className="text-sm leading-relaxed text-[var(--brand-charcoal)]">
          <p className="text-base font-semibold">Setări cookie</p>
          <p>
            Îți oferim controlul asupra cookie‑urilor. Cele esențiale sunt
            necesare, iar restul sunt opționale. Poți ajusta alegerile oricând.
          </p>
          <a
            href="/politica-cookie"
            className="mt-2 inline-block text-sm font-semibold text-[var(--brand-burnt)] underline"
          >
            Politica de cookie-uri
          </a>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={acceptEssentialOnly}
          className="rounded-full border border-[var(--border)] px-4 py-2 text-sm font-semibold text-[var(--brand-charcoal)] transition hover:bg-slate-50"
        >
          Acceptă esențiale
        </button>
        <button
          type="button"
          onClick={acceptAll}
          className="rounded-full bg-[var(--brand-burnt)] px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_18px_rgba(220,112,80,0.25)] transition hover:bg-[color-mix(in_srgb,var(--brand-burnt)_85%,#000_15%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-burnt)]"
        >
          Acceptă toate
        </button>
      </div>
    </div>
  );
}
